import { useState } from "react";
import { AtSign, Clock } from "lucide-react";
import { AnimatePresence } from "framer-motion";
import { Tooltip } from "@/ui-components/ui/tooltip";
import MentionMenu from "../../../chat/mentions/MentionMenu";
import EnhancedScopeTag from "../../../chat/input/EnhancedScopeTag";
import TimeFilter from "../../../chat/input/TimeFilter";
import TimeMenu from "../../../chat/input/TimeMenu";
import ContextIndicator from "../../../chat/input/ContextIndicator";
import type { RAGContext } from "@/hooks/useRAG";
import type { MentionItem } from "@/types/mention";
import type { MentionContext } from "@/types/mention";
import type { Project } from "@/types/dashboard";
import type { ConnectedTool } from "@/hooks/useConnectedTools";

interface CompactExpandedViewProps {
  context: RAGContext;
  mentionContext: MentionContext & { triggeredByButton?: boolean };
  projects: Project[];
  agents?: ConnectedTool[];
  selectedProjects: Set<string>;
  selectedBranches: Set<string>;
  sessionCount: number;
  projectsLoading: boolean;
  hasContext: boolean;
  onAtButtonClick: () => void;
  onMentionSelect: (item: MentionItem) => void;
  onMentionClose: () => void;
  onRemoveContextItem: (
    type: "project" | "branch" | "time",
    value: string
  ) => void;
  onClearContext: () => void;
  onUpdateTimeFilter: (filter: RAGContext["timeFilter"]) => void;
}

/**
 * Expanded view controls for CompactUnifiedSearch in chat mode
 * Shows context buttons, chips, and indicators
 */
export function CompactExpandedView({
  context,
  mentionContext,
  projects,
  agents = [],
  selectedProjects,
  selectedBranches,
  sessionCount,
  projectsLoading,
  hasContext,
  onAtButtonClick,
  onMentionSelect,
  onMentionClose,
  onRemoveContextItem,
  onClearContext,
  onUpdateTimeFilter,
}: CompactExpandedViewProps) {
  const [showTimeMenu, setShowTimeMenu] = useState(false);

  return (
    <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-border">
      <div className="flex items-center gap-2">
        {/* @ Button */}
        <div className="relative">
          <Tooltip content="Add project or branch context" side="bottom">
            <button
              onMouseDown={(e) => e.preventDefault()}
              onClick={onAtButtonClick}
              className="p-1.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
            >
              <AtSign className="w-4 h-4" />
            </button>
          </Tooltip>

          {/* Mention menu when triggered by button */}
          {mentionContext.showMenu &&
            mentionContext.triggeredByButton &&
            !projectsLoading && (
              <div className="absolute top-full left-0 mt-2 z-[150]">
                <MentionMenu
                  searchQuery={mentionContext.searchQuery}
                  onSelect={onMentionSelect}
                  onClose={onMentionClose}
                  projects={projects}
                  agents={agents}
                  recentBranches={Array.from(selectedBranches)}
                  selectedProjects={selectedProjects}
                  selectedBranches={selectedBranches}
                  selectedTimeFilter={context.timeFilter}
                  selectedAgents={context.agentNames || []}
                  isWrapped={true}
                />
              </div>
            )}
        </div>

        {/* Time filter button */}
        <div className="relative">
          <Tooltip content="Set time range" side="bottom">
            <button
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setShowTimeMenu(!showTimeMenu)}
              className={`p-1.5 rounded-full transition-all ${
                context.timeFilter
                  ? "bg-[#01B9D9]/10 text-[#01B9D9] hover:bg-[#01B9D9]/20"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
            >
              <Clock className="w-4 h-4" />
            </button>
          </Tooltip>

          {/* Time Menu */}
          <AnimatePresence>
            {showTimeMenu && (
              <div className="absolute top-full left-0 mt-2 [&>*]:!bottom-auto [&>*]:!top-0 [&>*]:!mb-0">
                <TimeMenu
                  currentFilter={context.timeFilter || null}
                  onSelectFilter={(filter) => {
                    onUpdateTimeFilter(filter);
                    setShowTimeMenu(false);
                  }}
                  onClose={() => setShowTimeMenu(false)}
                />
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Context chips */}
      {Array.from(selectedProjects).map((projectId) => {
        const projectData = projects.find((p) => p.id === projectId);
        return (
          <EnhancedScopeTag
            key={`project-${projectId}`}
            type="project"
            value={projectData?.name || projectId}
            onRemove={() => onRemoveContextItem("project", projectId)}
            projectData={projectData}
          />
        );
      })}

      {context.timeFilter && (
        <TimeFilter
          value={context.timeFilter}
          onClear={() => onRemoveContextItem("time", "")}
        />
      )}

      {hasContext && (
        <button
          onMouseDown={(e) => e.preventDefault()}
          onClick={onClearContext}
          className="text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded-full hover:bg-muted transition-colors"
        >
          Clear all
        </button>
      )}

      {hasContext && (
        <div className="ml-auto">
          <ContextIndicator
            sessionCount={sessionCount}
            projectCount={selectedProjects.size}
            isLoading={projectsLoading}
            hasFilters={hasContext}
            onClick={onAtButtonClick}
          />
        </div>
      )}
    </div>
  );
}
